import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sun, Moon, Monitor, AlertTriangle, LogOut } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import { useTheme } from '@/hooks/useTheme'
import type { Theme } from '@/hooks/useTheme'

const THEME_OPTIONS: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
]

function SectionHeader({ title, description }: { title: string; description?: string }) {
  return (
    <div className="mb-4">
      <h2 className="font-display text-lg font-semibold text-text-primary">{title}</h2>
      {description && <p className="mt-0.5 text-sm text-text-secondary">{description}</p>}
    </div>
  )
}

function Divider() {
  return <hr className="border-border" />
}

export const SettingsPage = () => {
  const { user, signOut } = useAuth()
  const { theme, setTheme, resolvedTheme } = useTheme()
  const navigate = useNavigate()
  const [avatarError, setAvatarError] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [signingOut, setSigningOut] = useState(false)

  const initials = user?.displayName
    ? user.displayName
        .split(' ')
        .filter(Boolean)
        .map((part) => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : (user?.email?.[0] || 'U').toUpperCase()

  const handleSignOut = useCallback(async () => {
    setSigningOut(true)
    try {
      await signOut()
      navigate('/login')
    } catch {
      setSigningOut(false)
    }
  }, [signOut, navigate])

  return (
    <div className="mx-auto max-w-2xl p-6 lg:p-8">
      <h1 className="font-display text-2xl font-bold text-text-primary mb-8">Settings</h1>

      <div className="space-y-8">
        {/* ── Profile ────────────────────────────────────────── */}
        <section>
          <SectionHeader title="Profile" description="Your account information" />
          <div className="rounded-xl border border-border bg-bg-elevated p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-accent-amber/10 text-xl font-bold text-accent-amber overflow-hidden">
                {user?.photoURL && !avatarError ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName || 'User'}
                    className="h-full w-full object-cover"
                    referrerPolicy="no-referrer"
                    onError={() => setAvatarError(true)}
                  />
                ) : (
                  <span>{initials}</span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-lg font-semibold text-text-primary truncate">
                  {user?.displayName || 'User'}
                </p>
                <p className="text-sm text-text-secondary truncate">{user?.email}</p>
                <p className="mt-1 text-xs font-medium uppercase tracking-wider text-accent-amber">
                  Founder
                </p>
              </div>
            </div>
          </div>
        </section>

        <Divider />

        {/* ── Appearance ─────────────────────────────────────── */}
        <section>
          <SectionHeader title="Appearance" description="Choose your preferred theme" />
          <div className="grid grid-cols-3 gap-3">
            {THEME_OPTIONS.map(({ value, label, icon: Icon }) => {
              const isActive = theme === value
              return (
                <button
                  key={value}
                  onClick={() => setTheme(value)}
                  className={`flex flex-col items-center gap-2 rounded-xl border p-4 transition-colors ${
                    isActive
                      ? 'border-accent-amber bg-accent-amber/10 text-accent-amber'
                      : 'border-border bg-bg-elevated text-text-secondary hover:border-border-hover hover:text-text-primary'
                  }`}
                >
                  <Icon size={20} />
                  <span className="text-sm font-medium">{label}</span>
                  {isActive && value === 'system' && (
                    <span className="text-[10px] opacity-60">({resolvedTheme})</span>
                  )}
                </button>
              )
            })}
          </div>
        </section>

        <Divider />

        {/* ── Account ────────────────────────────────────────── */}
        <section>
          <SectionHeader title="Account" />
          <div className="space-y-3">
            <Button
              variant="outline"
              onClick={handleSignOut}
              disabled={signingOut}
              className="w-full justify-start gap-2"
            >
              <LogOut size={16} />
              {signingOut ? 'Signing out...' : 'Sign out'}
            </Button>
          </div>
        </section>

        <Divider />

        {/* ── Danger Zone ────────────────────────────────────── */}
        <section>
          <SectionHeader
            title="Danger Zone"
            description="Irreversible actions on your account"
          />
          <div className="rounded-xl border border-accent-error/30 bg-accent-error/5 p-4">
            {!showDeleteConfirm ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text-primary">Delete Account</p>
                  <p className="text-xs text-text-secondary">
                    Permanently delete your account and all associated data.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="shrink-0 border-accent-error/30 text-accent-error hover:bg-accent-error/10"
                >
                  Delete
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0 text-accent-error" />
                  <p className="text-sm text-accent-error">
                    This action cannot be undone. All your context, documents, and settings will be
                    permanently deleted.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="bg-accent-error text-white hover:bg-accent-error/90"
                    onClick={() => {
                      // TODO: Wire to backend delete account endpoint
                      setShowDeleteConfirm(false)
                    }}
                  >
                    I understand, delete my account
                  </Button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Version */}
        <div className="pb-4 text-center text-xs text-text-tertiary">
          contxtmd v0.2.3
        </div>
      </div>
    </div>
  )
}
